import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Admin Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MembersPage from "./pages/MembersPage";
import CommitteesPage from "./pages/CommitteesPage";
import EventsPage from "./pages/EventsPage";
import SpotlightsPage from "./pages/SpotlightsPage";
import DonationsPage from "./pages/DonationsPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminManagementPage from "./pages/AdminManagementPage";
import NewsletterAdminPage from "./pages/NewsletterAdminPage";
import MemberEventsAdminPage from "./pages/MemberEventsAdminPage";
import BulkEmailPage from "./pages/BulkEmailPage";
import ContactsPage from "./pages/ContactsPage";

// Public Pages
import JoinPage from "./pages/JoinPage";
import FounderPage from "./pages/FounderPage";
import TeamPage from "./pages/TeamPage";
import StoryPage from "./pages/StoryPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import CommunityEventsPage from "./pages/CommunityEventsPage";
import ResourcesPage from "./pages/ResourcesPage";
import NewsletterPage from "./pages/NewsletterPage";
import NewsletterApril2026Page from "./pages/NewsletterApril2026Page";
import NewsletterMarch2026Page from "./pages/NewsletterMarch2026Page";
import NewsletterFeb2026Page from "./pages/NewsletterFeb2026Page";
import CommunityConnectPage from "./pages/CommunityConnectPage";

// Member Pages
import MemberRegisterPage from "./pages/MemberRegisterPage";
import MemberLoginPage from "./pages/MemberLoginPage";
import MemberResetPasswordPage from "./pages/MemberResetPasswordPage";
import MemberDashboardPage from "./pages/MemberDashboardPage";
import MemberDirectoryPage from "./pages/MemberDirectoryPage";
import MemberViewProfilePage from "./pages/MemberViewProfilePage";
import MemberMessagesPage from "./pages/MemberMessagesPage";
import MemberEventsPage from "./pages/MemberEventsPage";
import MemberProfilePage from "./pages/MemberProfilePage";
import MemberSettingsPage from "./pages/MemberSettingsPage";
import MemberSpotlightsPage from "./pages/MemberSpotlightsPage";
import MemberBrother2BrotherPage from "./pages/MemberBrother2BrotherPage";

// Components
import Layout from "./components/Layout";
import MemberLayout from "./components/MemberLayout";

// Admin Protected Route wrapper
const AdminRoute = ({ children }) => {
    const { token, userType, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex items-center justify-center">
                <div className="text-[#A0A0A0] animate-pulse">Loading...</div>
            </div>
        );
    }
    
    if (!token || userType !== 'admin') {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Member Protected Route wrapper
const MemberRoute = ({ children }) => {
    const { token, userType, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex items-center justify-center">
                <div className="text-[#A0A0A0] animate-pulse">Loading...</div>
            </div>
        );
    }
    
    if (!token || userType !== 'member') {
        return <Navigate to="/member/login" replace />;
    }
    
    return children;
};

// Public Route wrapper (redirect if already logged in as admin)
const PublicAdminRoute = ({ children }) => {
    const { token, userType, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex items-center justify-center">
                <div className="text-[#A0A0A0] animate-pulse">Loading...</div>
            </div>
        );
    }
    
    if (token && userType === 'admin') {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

// Public Member Route wrapper (redirect if already logged in as member)
const PublicMemberRoute = ({ children }) => {
    const { token, userType, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex items-center justify-center">
                <div className="text-[#A0A0A0] animate-pulse">Loading...</div>
            </div>
        );
    }
    
    if (token && userType === 'member') {
        return <Navigate to="/member/dashboard" replace />;
    }
    
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Pages */}
            <Route path="/join" element={<JoinPage />} />
            
            {/* Who We Are Pages */}
            <Route path="/who-we-are/founder" element={<FounderPage />} />
            <Route path="/who-we-are/team" element={<TeamPage />} />
            <Route path="/who-we-are/story" element={<StoryPage />} />
            <Route path="/who-we-are/testimonials" element={<TestimonialsPage />} />
            
            {/* In The Community Pages */}
            <Route path="/community/events" element={<CommunityEventsPage />} />
            <Route path="/community/resources" element={<ResourcesPage />} />
            <Route path="/community/newsletter" element={<NewsletterPage />} />
            <Route path="/community/newsletter/april-2026" element={<NewsletterApril2026Page />} />
            <Route path="/community/newsletter/march-2026" element={<NewsletterMarch2026Page />} />
            <Route path="/community/newsletter/february-2026" element={<NewsletterFeb2026Page />} />
            <Route path="/community/connect" element={<CommunityConnectPage />} />
            
            {/* Admin Auth Routes */}
            <Route 
                path="/login" 
                element={
                    <PublicAdminRoute>
                        <LoginPage />
                    </PublicAdminRoute>
                } 
            />

            {/* Member Auth Routes */}
            <Route 
                path="/member/register" 
                element={
                    <PublicMemberRoute>
                        <MemberRegisterPage />
                    </PublicMemberRoute>
                } 
            />
            <Route 
                path="/member/login" 
                element={
                    <PublicMemberRoute>
                        <MemberLoginPage />
                    </PublicMemberRoute>
                } 
            />
            <Route 
                path="/member/reset-password" 
                element={<MemberResetPasswordPage />}
            />

            {/* Admin Protected Routes */}
            <Route 
                element={
                    <AdminRoute>
                        <Layout />
                    </AdminRoute>
                }
            >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/analytics" element={<AdminAnalyticsPage />} />
                <Route path="/members" element={<MembersPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/committees" element={<CommitteesPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/newsletters" element={<NewsletterAdminPage />} />
                <Route path="/member-events" element={<MemberEventsAdminPage />} />
                <Route path="/bulk-email" element={<BulkEmailPage />} />
                <Route path="/spotlights" element={<SpotlightsPage />} />
                <Route path="/donations" element={<DonationsPage />} />
                <Route path="/admin-management" element={<AdminManagementPage />} />
            </Route>

            {/* Member Protected Routes */}
            <Route 
                element={
                    <MemberRoute>
                        <MemberLayout />
                    </MemberRoute>
                }
            >
                <Route path="/member/dashboard" element={<MemberDashboardPage />} />
                <Route path="/member/directory" element={<MemberDirectoryPage />} />
                <Route path="/member/directory/:memberId" element={<MemberViewProfilePage />} />
                <Route path="/member/messages" element={<MemberMessagesPage />} />
                <Route path="/member/events" element={<MemberEventsPage />} />
                <Route path="/member/spotlights" element={<MemberSpotlightsPage />} />
                <Route path="/member/brother2brother" element={<MemberBrother2BrotherPage />} />
                <Route path="/member/profile" element={<MemberProfilePage />} />
                <Route path="/member/settings" element={<MemberSettingsPage />} />
            </Route>

            {/* Redirect root to join page for public access */}
            <Route path="/" element={<Navigate to="/join" replace />} />
            
            {/* Catch all - redirect to join */}
            <Route path="*" element={<Navigate to="/join" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
